import { useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import {
  Box,
  Card,
  CardContent,
  EditIcon,
  IconButton,
  SearchInput,
  Table,
  Typography
} from "@dealer-center/core-components";
import type { TypeSafeTableColumns } from "@dealer-center/core-components";
import type { NrtRecord } from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";
import { StatusChip } from "./StatusChip";
import {
  EMPTY_VALUE_PLACEHOLDER,
  compareNrtDates,
  formatNrtDate
} from "./nrtDate";

// Exported so specs assert against the real value instead of a copy that drifts.
export const PAGE_SIZE = 10;
// Floor so columns keep enough room on narrow screens instead of squeezing.
const TABLE_SCROLL_X = 1550;

export interface RecordsTableProps {
  records: NrtRecord[];
  isLoading?: boolean;
  onEditRecord: (record: NrtRecord) => void;
}

// Only fields shown as columns below (id/caseNumber/createdBy excluded).
const SEARCHABLE_FIELDS = [
  "accountId",
  "providerId",
  "status",
  "type",
  "classification",
  "select",
  "map",
  "notes",
  "tags",
  "dateCreated",
  "expirationDate",
  "lastAccessed"
] as const satisfies readonly (keyof NrtRecord)[];

// Matched against displayed text, not the stored value: cells show
// "Jun 11, 2026" while the record holds "1781151838".
const DATE_FIELDS = new Set<keyof NrtRecord>([
  "dateCreated",
  "expirationDate",
  "lastAccessed"
]);

const matchesSearchTerm = (record: NrtRecord, term: string) =>
  SEARCHABLE_FIELDS.some(field => {
    const value = record[field];
    if (value === null || value === undefined) {
      return false;
    }
    const text = DATE_FIELDS.has(field) ? formatNrtDate(value) : String(value);
    return text.toLowerCase().includes(term);
  });

// unknown (not string): N-typed attributes arrive as numbers, and blank
// strings like `tags: ""` need the placeholder too, not just null/undefined.
const renderOrPlaceholder = (value: unknown) => {
  const text =
    value === null || value === undefined ? "" : String(value).trim();
  return text || EMPTY_VALUE_PLACEHOLDER;
};

type CellComparator = (a: unknown, b: unknown) => number;

const compareStrings: CellComparator = (a, b) =>
  String(a ?? "").localeCompare(String(b ?? ""));

// Date columns pass compareNrtDates; a text sort would order by month name.
const sortByField =
  (field: keyof NrtRecord, compare: CellComparator = compareStrings) =>
  (a: NrtRecord, b: NrtRecord) =>
    compare(a[field], b[field]);

// A factory rather than a module-level array: the Actions column closes over
// onEditRecord, which only exists inside the component.
const buildColumns = (
  onEditRecord: (record: NrtRecord) => void
): TypeSafeTableColumns<NrtRecord> => [
  {
    dataIndex: "accountId",
    title: "Account ID",
    sorter: sortByField("accountId")
  },
  {
    dataIndex: "providerId",
    title: "Provider ID",
    sorter: sortByField("providerId")
  },
  {
    dataIndex: "status",
    title: "Status",
    render: (status: NrtRecord["status"]) => <StatusChip status={status} />,
    sorter: sortByField("status")
  },
  {
    dataIndex: "type",
    title: "Type",
    render: renderOrPlaceholder,
    sorter: sortByField("type")
  },
  {
    dataIndex: "classification",
    title: "Classification",
    render: renderOrPlaceholder,
    sorter: sortByField("classification")
  },
  {
    dataIndex: "select",
    title: "Select",
    render: renderOrPlaceholder,
    sorter: sortByField("select")
  },
  {
    dataIndex: "map",
    title: "Map",
    render: renderOrPlaceholder,
    sorter: sortByField("map")
  },
  {
    dataIndex: "expirationDate",
    title: "Expiration Date",
    render: formatNrtDate,
    sorter: sortByField("expirationDate", compareNrtDates)
  },
  {
    dataIndex: "dateCreated",
    title: "Date Created",
    render: formatNrtDate,
    sorter: sortByField("dateCreated", compareNrtDates)
  },
  {
    dataIndex: "lastAccessed",
    title: "Last Accessed",
    render: formatNrtDate,
    sorter: sortByField("lastAccessed", compareNrtDates)
  },
  {
    dataIndex: "notes",
    title: "Notes",
    render: renderOrPlaceholder,
    sorter: sortByField("notes")
  },
  {
    dataIndex: "tags",
    title: "Tags",
    render: renderOrPlaceholder,
    sorter: sortByField("tags")
  },
  // Last so the edit button sits at the right edge, after every data column.
  {
    title: "Actions",
    columnSortable: false,
    render: (_value: unknown, record: NrtRecord) => (
      <IconButton
        aria-label={`Edit case ${record.caseNumber} for ${record.accountId}`}
        buttonStyle="tertiary"
        size="sm"
        onClick={() => onEditRecord(record)}
      >
        <EditIcon title="Edit" />
      </IconButton>
    )
  }
];

export const RecordsTable = ({
  records,
  isLoading = false,
  onEditRecord
}: RecordsTableProps) => {
  const [searchValue, setSearchValue] = useState("");

  const columns = useMemo(() => buildColumns(onEditRecord), [onEditRecord]);

  const filteredRecords = useMemo(() => {
    const term = searchValue.trim().toLowerCase();
    if (!term) {
      return records;
    }
    return records.filter(record => matchesSearchTerm(record, term));
  }, [records, searchValue]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value ?? "");
  };

  return (
    // minWidth: 0 lets this flex item shrink instead of forcing the page
    // (a flex column) to overflow to fit the table's scrollX width.
    <Card
      content={
        <CardContent>
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 2
            }}
          >
            <Typography sx={{ fontWeight: "bold" }} variant="h4">
              Mapping Records {isLoading ? "" : `(${filteredRecords.length})`}
            </Typography>
            <SearchInput
              data-testid="nrt-mapping-tool-search-input"
              inputValue={searchValue}
              placeholder="Search records..."
              sx={{ maxWidth: "300px" }}
              onClearCallback={() => setSearchValue("")}
              onInputChange={handleSearchChange}
            />
          </Box>
          <Table.Table
            sortableColumns
            columns={columns}
            currentPageSize={PAGE_SIZE}
            data={filteredRecords}
            dataDensity="small"
            emptyText="No mapping records found"
            isLoading={isLoading}
            rowKey="id"
            scrollX={TABLE_SCROLL_X}
            tableLayout="fixed"
          />
        </CardContent>
      }
      sx={{ minWidth: 0 }}
    />
  );
};
